import type { Locale } from './locale';

/**
 * Parses what people type into amount fields and returns a plain decimal string the API accepts
 * ("1234.56"), or null when it isn't a number. Spaces, € and % are ignored.
 *
 * With both separators present, the last one is the decimal point. With only commas, English
 * reads them as thousands ("1,000" = 1000) and Bulgarian as the decimal comma ("81,50" = 81.5).
 * A lone dot is always a decimal point.
 */
export function parseDecimal(input: string, locale: Locale): string | null {
  let text = input.replace(/[\s\u00a0\u202f€%]/g, '').replace(/−/g, '-');
  if (text === '' || text === '-') return null;
  const lastComma = text.lastIndexOf(',');
  const lastDot = text.lastIndexOf('.');
  if (lastComma >= 0 && lastDot >= 0) {
    const decimal = lastComma > lastDot ? ',' : '.';
    const group = decimal === ',' ? '.' : ',';
    text = text.split(group).join('').replace(decimal, '.');
  } else if (lastComma >= 0) {
    text = locale === 'en' ? text.split(',').join('') : text.replace(',', '.');
  }
  if (!/^-?\d+(\.\d+)?$/.test(text)) return null;
  return text.replace(/^(-?)0+(?=\d)/, '$1');
}

export function isPositive(value: string | null): value is string {
  return value !== null && Number(value) > 0;
}

/** Rounds a decimal string to the instrument's tick ("138.1291", "0.01" → "138.13"). */
export function roundToTick(value: string, tick: string): string {
  const places = tick.includes('.') ? (tick.split('.')[1] ?? '').length : 0;
  const scale = 10 ** places;
  const steps = Math.round((Number(value) * scale) / Math.round(Number(tick) * scale));
  const units = steps * Math.round(Number(tick) * scale);
  const whole = Math.trunc(units / scale);
  const frac = Math.abs(units % scale);
  return places === 0 ? String(whole) : `${String(whole)}.${String(frac).padStart(places, '0')}`;
}
